import React from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { Input } from '@planne-software/uni/Input';
import { PhoneInput } from '@planne-software/uni/PhoneInput';
import { IconCalendar } from '@planne-software/uni/icons';

import { SaleFormInput, FormInputInputType } from '@src/core/buyerApiClient';
import { useTranslate } from '@src/core/hooks';
import { applyMask } from '@src/common/masks';
import { tariffFieldSize } from '@src/features/orders/form-inputs/styles';
import { Grid } from '@planne-software/uni/Grid';
import { resolveFormInputPlaceholder } from '@src/features/orders/form-inputs/core/resolveFormInputPlaceholder';

import type { TariffFormValues } from '@src/features/orders/form-inputs/core/tariffFormInputsSchema';

type TariffFormFieldsProps = {
  inputs: SaleFormInput[];
  control: Control<TariffFormValues>;
  errors: FieldErrors<TariffFormValues>;
};

const maskCpf = (value: string) => applyMask(value, 'cpf');
const maskBirthdate = (value: string) => applyMask(value, 'birthdate');

type FieldKind = 'text' | 'phone' | 'date';

const resolveFieldKind = (input: SaleFormInput): FieldKind => {
  if (input.inputType === FormInputInputType.Phone) {
    return 'phone';
  }

  if (input.inputType === FormInputInputType.Date) {
    return 'date';
  }

  return 'text';
};

/** The design system's `DatePicker` is an inline calendar; the design asks for a field. */
const resolveMask = (input: SaleFormInput) => {
  if (input.inputType === FormInputInputType.Date) {
    return maskBirthdate;
  }

  return (input.validationRules ?? []).includes('cpf_format') ? maskCpf : undefined;
};

export const TariffFormFields = (props: TariffFormFieldsProps) => {
  const { inputs, control, errors } = props;
  const { i18n } = useTranslate();

  return (
    <>
      {inputs.map((input) => {
        const kind = resolveFieldKind(input);
        const placeholderKey = resolveFormInputPlaceholder(input.validationRules ?? null);
        const placeholder = placeholderKey ? i18n(placeholderKey) : undefined;

        return (
          <Grid size={tariffFieldSize} key={input.hash} data-testid={`tariff-field-${input.hash}`} data-kind={kind}>
            <Controller
              name={input.hash}
              control={control}
              render={({ field: { ref, ...field } }) =>
                kind === 'phone' ? (
                  // PhoneInput has its own contract: its onChange hands over
                  // `{ phone, country }`, not an event. Spreading the react-hook-form field
                  // would make the whole object the value, and react-international-phone
                  // throws calling `startsWith` on it in the next render.
                  <PhoneInput
                    ref={ref}
                    name={field.name}
                    value={typeof field.value === 'string' ? field.value : ''}
                    onChange={(next) => field.onChange(next.phone)}
                    onBlur={field.onBlur}
                    label={input.formInput.name}
                    required={input.formInput.required}
                    placeholder={placeholder}
                    error={!!errors[input.hash]}
                    helperText={errors[input.hash]?.message}
                    size='medium'
                  />
                ) : (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    id={field.name}
                    inputRef={ref}
                    label={input.formInput.name}
                    required={input.formInput.required}
                    placeholder={placeholder}
                    mask={resolveMask(input)}
                    type={input.inputType === FormInputInputType.Email ? 'email' : 'text'}
                    inputMode={kind === 'date' ? 'numeric' : undefined}
                    disableClearable
                    error={!!errors[input.hash]}
                    helperText={errors[input.hash]?.message}
                    size='medium'
                    slotProps={
                      kind === 'date' ? { input: { endAdornment: <IconCalendar size={20} aria-hidden /> } } : undefined
                    }
                  />
                )
              }
            />
          </Grid>
        );
      })}
    </>
  );
};

export default TariffFormFields;
